// THE KNOWLEDGE GRAPH'S CONNECTIONS, per item (busy-my-web#710). One entry per panel key
// (`<type>:<refId>`, lib/graphConnections `panelKey`), so two readers of the same item — a task's
// page and the box over it — agree about what is suggested and what has been answered.
//
//   status: 'loading' | 'ready' | 'missing' | 'error'
//     · 'missing' — the BFF answered 404: a BFF from before busy-main-bff#60 (or graph off on that
//       environment). The panel hides QUIETLY; nothing about it is an error the user can act on.
//     · 'error'   — any other failure. Also hidden: connections are an aid on a reader, and a red
//       line under a task about a service the user never asked for reads as the task being broken.
//
// The answers go to the BFF (`v1/connections/{edgeId}/confirm|reject`, `POST v1/connections`) and
// the entry moves with them: a confirmed edge leaves the suggestions for the confirmed group, a
// rejected one leaves altogether (and is put back when the call fails). "Was this helpful?" is
// LOGGED here; lib/connectionItem `recordConnectionFeedback` also posts it, fire-and-forget, to
// `POST v1/connections/{edgeId}/feedback`.
//
// Two more reads for the journey around the panel:
//   · the USER-WIDE suggestions of one type (`GET v1/connections?state=suggested&type=email_thread`)
//     — the Email workspace Briefing's "N Possible connections" and the list it opens. Filed as one
//     more entry (`inboxKey(type)`), so an answer given on a reader moves it too, and vice versa;
//     each item also carries its `source` node (the email);
//   · one item's COUNTS (`GET v1/connections/summary/{type}/{refId}` → `{ connected, possible }`) —
//     the Ongoing Briefing's "N Connections". Absent (no entry) on a 404.

import api from '@/lib/services/general.service'
import { inboxKey, normalizeConnections, normalizeEdge, panelKey } from '@/lib/graphConnections'

/** How many "Was this helpful?" answers the client keeps — a log, not a store of record. */
export const FEEDBACK_LOG_SIZE = 50

export const state = {
  byKey: {},
  summaries: {},
  feedback: [],
}

const EMPTY = Object.freeze({ status: 'idle', node: null, connections: [] })

export const getters = {
  /** The entry for one item, or an idle empty one. */
  entry: (s) => (type, refId) => s.byKey[panelKey(type, refId)] || EMPTY,
  feedback: (s) => s.feedback,
  /** The user-wide suggestions entry of one type (`email_thread` by default). */
  inbox: (s) => (type = 'email_thread') => s.byKey[inboxKey(type)] || EMPTY,
  /** One item's `{ connected, possible }`, or null when unknown. */
  summary: (s) => (type, refId) => s.summaries[panelKey(type, refId)] || null,
}

export const mutations = {
  SET_ENTRY(s, { key, entry }) {
    s.byKey = { ...s.byKey, [key]: { ...EMPTY, ...(s.byKey[key] || {}), ...entry } }
  },
  /** Replace one edge (matched by id) in every entry holding it, or drop it when `edge` is null. */
  PUT_EDGE(s, { edgeId, edge }) {
    const next = {}
    for (const [key, entry] of Object.entries(s.byKey)) {
      const has = (entry.connections || []).some((c) => c.edgeId === edgeId)
      if (!has) { next[key] = entry; continue }
      const connections = edge
        ? entry.connections.map((c) => (c.edgeId === edgeId ? edge : c))
        : entry.connections.filter((c) => c.edgeId !== edgeId)
      next[key] = { ...entry, connections }
    }
    s.byKey = next
  },
  SET_SUMMARY(s, { key, summary }) {
    const next = { ...s.summaries }
    if (summary) next[key] = summary
    else delete next[key]
    s.summaries = next
  },
  /** Put an edge back into one entry (a failed "Not related"). */
  RESTORE_EDGE(s, { key, edge, index }) {
    const entry = s.byKey[key]
    if (!entry || entry.connections.some((c) => c.edgeId === edge.edgeId)) return
    const connections = [...entry.connections]
    connections.splice(Math.min(Math.max(index, 0), connections.length), 0, edge)
    s.byKey = { ...s.byKey, [key]: { ...entry, connections } }
  },
  LOG_FEEDBACK(s, item) {
    s.feedback = [...s.feedback, item].slice(-FEEDBACK_LOG_SIZE)
  },
}

const httpStatus = (err) => Number(err?.response?.status) || 0

/** The edge as the server returned it, merged over what was there, with the new state. */
const answered = (edge, res, nextState) => {
  const back = normalizeEdge(res?.data?.data ?? res?.data)
  return { ...edge, ...(back && back.edgeId === edge.edgeId ? back : {}), state: nextState, node: (back && back.node) || edge.node }
}

/** Every place one edge is filed — a reader's entry and the user-wide inbox can both hold it. */
const findEdge = (s, edgeId) => {
  const found = []
  for (const [key, entry] of Object.entries(s.byKey)) {
    const index = (entry.connections || []).findIndex((c) => c.edgeId === edgeId)
    if (index >= 0) found.push({ key, index, edge: entry.connections[index] })
  }
  return found
}

export const actions = {
  /** Read one item's connections. Resolves to the entry's status. */
  async fetch({ commit }, { type, refId } = {}) {
    const key = panelKey(type, refId)
    if (!key) return 'missing'
    commit('SET_ENTRY', { key, entry: { status: 'loading' } })
    try {
      const res = await api.getConnections(type, refId)
      const { node, connections } = normalizeConnections(res)
      commit('SET_ENTRY', { key, entry: { status: 'ready', node, connections } })
      return 'ready'
    } catch (err) {
      const status = httpStatus(err) === 404 ? 'missing' : 'error'
      commit('SET_ENTRY', { key, entry: { status, node: null, connections: [] } })
      return status
    }
  },

  /** The user-wide suggestions of one type. Resolves to the entry's status. */
  async fetchInbox({ commit }, { type = 'email_thread' } = {}) {
    const key = inboxKey(type)
    commit('SET_ENTRY', { key, entry: { status: 'loading' } })
    try {
      const res = await api.getSuggestedConnections(type)
      const { connections } = normalizeConnections(res)
      commit('SET_ENTRY', { key, entry: { status: 'ready', node: null, connections } })
      return 'ready'
    } catch (err) {
      const status = httpStatus(err) === 404 ? 'missing' : 'error'
      commit('SET_ENTRY', { key, entry: { status, node: null, connections: [] } })
      return status
    }
  },

  /** One item's counts. Resolves to `{ connected, possible }`, or null when unknown. */
  async fetchSummary({ commit }, { type, refId } = {}) {
    const key = panelKey(type, refId)
    if (!key) return null
    try {
      const res = await api.getConnectionSummary(type, refId)
      const body = res?.data?.data ?? res?.data ?? {}
      const summary = { connected: Number(body.connected) || 0, possible: Number(body.possible) || 0 }
      commit('SET_SUMMARY', { key, summary })
      return summary
    } catch {
      commit('SET_SUMMARY', { key, summary: null })
      return null
    }
  },

  /** Connect — confirm a suggested edge. True when it landed. */
  async confirm({ commit, state: s }, edgeId) {
    const found = findEdge(s, edgeId)
    if (!found.length) return false
    try {
      const res = await api.confirmConnection(edgeId)
      commit('PUT_EDGE', { edgeId, edge: answered(found[0].edge, res, 'confirmed') })
      return true
    } catch {
      return false
    }
  },

  /**
   * Not related — drop the edge at once and tell the server; put it back if the call failed.
   * Also the notice's Undo after a Connect: the graph has one way to take a confirmed link back
   * out, and it is this one (the in-Ongoing table's Undo records the same — see
   * PossibleConnectionsTable.vue's note).
   */
  async reject({ commit, state: s }, { edgeId, reason = null } = {}) {
    const found = findEdge(s, edgeId)
    if (!found.length) return false
    commit('PUT_EDGE', { edgeId, edge: null })
    try {
      await api.rejectConnection(edgeId, reason)
      return true
    } catch {
      found.forEach((place) => commit('RESTORE_EDGE', place))
      return false
    }
  },

  /** Connect two items by hand (`{ a: { type, refId }, b: { type, refId } }`), then re-read `a`. */
  async connect({ dispatch }, { a, b } = {}) {
    if (!panelKey(a?.type, a?.refId) || !panelKey(b?.type, b?.refId)) return false
    try {
      await api.connectItems({ type: a.type, refId: a.refId }, { type: b.type, refId: b.refId })
    } catch {
      return false
    }
    await dispatch('fetch', a)
    return true
  },

  /** "Was this helpful?" — the client's log (the server copy is posted by lib/connectionItem). */
  logFeedback({ commit }, { edgeId, value, type, refId } = {}) {
    if (!edgeId) return
    commit('LOG_FEEDBACK', { edgeId: String(edgeId), value: value || '', key: panelKey(type, refId), at: new Date().toISOString() })
  },
}
